import React from 'react';
import { toast } from 'react-hot-toast';
import { StarIcon as CrownIcon, Bars3Icon } from '@heroicons/react/24/solid';
import { UserIcon, QrCodeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import useEventStore from '../stores/eventStore';
import { useRoleStore } from '../stores/roleStore';
import Modal from './ui/Modal';
import InvitePanel from './invite/InvitePanel';
import JoinRequestsPanel from './party/JoinRequestsPanel';
import { endParty, leaveParty } from '../services/eventLifecycle';
import { watchJoinRequests } from '../services/joinRequests';
import { isEventHost } from '../utils/identity';
import { isEventLive } from '../utils/eventStatus';
import { describeError } from '../utils/errorLog';

export default function EventHeader({ onOpenPanel, onEnded, arrivalInvite = false }) {
  const eventData = useEventStore(state => state.eventData);
  const userRole = useRoleStore(state => state.userRole);
  const userId = useRoleStore(state => state.userId);
  const hostId = useRoleStore(state => state.hostId);
  const [inviteOpen, setInviteOpen] = React.useState(arrivalInvite);
  const [inviteMode, setInviteMode] = React.useState(arrivalInvite ? 'arrival' : 'share');
  const [endOpen, setEndOpen] = React.useState(false);
  const [ending, setEnding] = React.useState(false);
  const [leaveOpen, setLeaveOpen] = React.useState(false);
  const [leaving, setLeaving] = React.useState(false);
  const [joinRequests, setJoinRequests] = React.useState([]);

  const host = isEventHost(userId, hostId);
  const live = isEventLive(eventData);

  React.useEffect(() => {
    if (!host || !eventData?.id || !live) {
      setJoinRequests([]);
      return undefined;
    }
    return watchJoinRequests(eventData.id, setJoinRequests);
  }, [host, eventData?.id, live]);

  if (!eventData) return null;

  const handleEndParty = async () => {
    if (ending) return;
    setEnding(true);
    try {
      await endParty(eventData.id);
      setEndOpen(false);
      onEnded?.();
    } catch (error) {
      console.error('Failed to end party:', describeError(error));
      toast.error(error?.code === 'not-host' ? 'Only the host can end this party' : 'Could not end the party');
    } finally {
      setEnding(false);
    }
  };

  const handleLeaveParty = async () => {
    if (leaving) return;
    setLeaving(true);
    try {
      await leaveParty(eventData.id);
      setLeaveOpen(false);
      onEnded?.();
    } catch (error) {
      console.error('Failed to leave party:', describeError(error));
      toast.error('Could not leave the party');
    } finally {
      setLeaving(false);
    }
  };

  return (
    <>
      <header className="w-full bg-gradient-to-b from-black/60 to-transparent px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-3 backdrop-blur-sm border-b border-gray-800 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={onOpenPanel}
            className="md:hidden shrink-0 p-2 -ml-1 rounded-lg text-white hover:bg-white/10"
            aria-label="Open menu"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg md:text-2xl font-bold text-white truncate">{eventData?.title}</h1>
            <p className="text-xs text-gray-400 md:hidden truncate">
              Code <span className="font-mono text-white">{eventData?.id}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3 md:space-x-6 text-sm shrink-0">
          <button
            type="button"
            onClick={() => {
              setInviteMode('share');
              setInviteOpen(true);
            }}
            aria-label="Invite people"
            className="flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1.5 text-white hover:bg-white/20 transition-colors"
          >
            <QrCodeIcon className="w-5 h-5" />
            <span className="hidden sm:inline">Invite</span>
          </button>

          <div className="flex items-center">
            {userRole === 'host' ? (
              <>
                <CrownIcon className="w-5 h-5 text-red-500 mr-2" />
                <span className="text-red-500 font-medium">Host</span>
              </>
            ) : (
              <>
                <UserIcon className="w-5 h-5 text-purple-500 mr-2" />
                <span className="text-purple-500 font-medium">Guest</span>
              </>
            )}
          </div>

          <div className="hidden md:flex items-center">
            <span className="text-gray-400">Event Code:</span>
            <span className="text-white ml-2 font-mono">{eventData?.id}</span>
          </div>

          {live && host && (
            <button
              type="button"
              onClick={() => setEndOpen(true)}
              aria-label="End party"
              className="rounded-lg p-1.5 text-gray-400 hover:bg-white/10 hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          )}

          {live && !host && (
            <button
              type="button"
              onClick={() => setLeaveOpen(true)}
              aria-label="Leave party"
              className="rounded-lg p-1.5 text-gray-400 hover:bg-white/10 hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          )}
        </div>
      </header>

      {host && live && (
        <JoinRequestsPanel
          eventId={eventData.id}
          hostId={eventData.hostId}
          requests={joinRequests}
        />
      )}

      <Modal
        open={inviteOpen}
        title="Invite people"
        onClose={() => setInviteOpen(false)}
      >
        <div className="space-y-5">
          <InvitePanel
            eventCode={eventData?.id}
            eventTitle={eventData?.title}
            joinMode={eventData?.settings?.joinMode}
          />
          {inviteMode === 'arrival' && (
            <button
              type="button"
              onClick={() => setInviteOpen(false)}
              className="w-full py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
            >
              Continue to party
            </button>
          )}
        </div>
      </Modal>

      <Modal
        open={endOpen}
        title={`End “${eventData.title}”?`}
        onClose={() => setEndOpen(false)}
        size="sm"
        footer={(
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEndOpen(false)}
              className="flex-1 rounded-lg bg-white/10 py-2 text-sm text-white hover:bg-white/20"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleEndParty}
              disabled={ending}
              className="flex-1 rounded-lg bg-red-600 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
            >
              {ending ? 'Ending…' : 'End party'}
            </button>
          </div>
        )}
      >
        <p className="text-sm text-gray-300">
          Guests will no longer be able to join or add songs.
          The party will remain in Previous Sessions.
        </p>
      </Modal>

      <Modal
        open={leaveOpen}
        title="Leave party?"
        onClose={() => setLeaveOpen(false)}
        size="sm"
        footer={(
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setLeaveOpen(false)}
              className="flex-1 rounded-lg bg-white/10 py-2 text-sm text-white hover:bg-white/20"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleLeaveParty}
              disabled={leaving}
              className="flex-1 rounded-lg bg-red-600 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
            >
              {leaving ? 'Leaving…' : 'Leave Party'}
            </button>
          </div>
        )}
      >
        <p className="text-sm text-gray-300">
          You&apos;ll leave this party, but the party will continue for everyone else.
        </p>
      </Modal>
    </>
  );
}
